import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { workerTeamsAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Users,
  Plus,
  MagnifyingGlass,
  MapPin,
  Star,
  Crown,
  Briefcase,
  SpinnerGap,
  ArrowRight,
  UserPlus,
  Envelope,
  Check,
  X
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const WorkerTeams = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ledTeams, setLedTeams] = useState([]);
  const [memberTeams, setMemberTeams] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [processingInvite, setProcessingInvite] = useState(null);

  useEffect(() => {
    fetchTeams();
    fetchPendingInvites();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await workerTeamsAPI.getMyTeams();
      if (response.data.success) {
        setLedTeams(response.data.led_teams || []);
        setMemberTeams(response.data.member_teams || []);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast({
        title: 'Error',
        description: 'Failed to load your teams',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingInvites = async () => {
    try {
      setLoadingInvites(true);
      const response = await workerTeamsAPI.getPendingInvites();
      if (response.data.success) {
        setPendingInvites(response.data.invites || []);
      }
    } catch (error) {
      console.error('Error fetching invites:', error);
    } finally {
      setLoadingInvites(false);
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    try {
      setProcessingInvite(inviteId);
      const response = await workerTeamsAPI.acceptInvite(inviteId);
      if (response.data.success) {
        toast({
          title: 'Joined Team',
          description: response.data.message
        });
        fetchTeams();
        fetchPendingInvites();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to accept invite',
        variant: 'destructive'
      });
    } finally {
      setProcessingInvite(null);
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    try {
      setProcessingInvite(inviteId);
      const response = await workerTeamsAPI.declineInvite(inviteId);
      if (response.data.success) {
        toast({
          title: 'Invite Declined',
          description: 'The invitation has been declined'
        });
        fetchPendingInvites();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to decline invite',
        variant: 'destructive'
      });
    } finally {
      setProcessingInvite(null);
    }
  };

  const TeamCard = ({ team, isLeader }) => (
    <div 
      className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000] hover:shadow-[6px_6px_0px_#000] transition-all cursor-pointer"
      onClick={() => navigate(`/worker-teams/${team.id}`)}
      data-testid={`team-card-${team.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center border-2 ${
            isLeader ? 'bg-amber-100 border-amber-400' : 'bg-blue-100 border-blue-400'
          }`}>
            <Users size={24} weight="bold" className={isLeader ? 'text-amber-600' : 'text-blue-600'} />
          </div>
          <div>
            <h3 className="font-black text-lg text-gray-900">{team.name}</h3>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Users size={14} weight="bold" />
              <span>{team.member_count} members</span>
              {isLeader && (
                <Badge className="bg-amber-100 text-amber-700 border border-amber-400 ml-2">
                  <Crown size={12} weight="fill" className="mr-1" />
                  Leader
                </Badge>
              )}
            </div>
          </div>
        </div>
        {team.rating > 0 && (
          <div className="flex items-center gap-1 text-amber-500">
            <Star size={16} weight="fill" />
            <span className="font-bold">{team.rating.toFixed(1)}</span>
          </div>
        )}
      </div>
      
      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{team.description}</p>
      
      {team.skills && team.skills.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {team.skills.slice(0, 4).map((skill) => (
            <Badge key={skill} variant="secondary" className="text-xs">
              {skill}
            </Badge>
          ))}
          {team.skills.length > 4 && (
            <Badge variant="secondary" className="text-xs">
              +{team.skills.length - 4} more
            </Badge>
          )}
        </div>
      )}
      
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          {team.location && (
            <>
              <MapPin size={14} weight="bold" />
              <span>{team.location}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 text-[#2B4C6F] font-bold text-sm">
          View Team <ArrowRight size={16} weight="bold" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F0F2] flex items-center justify-center">
        <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-24" data-testid="worker-teams-page">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white py-8 border-b-4 border-black">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-black">My Teams</h1>
          <p className="text-blue-100 text-lg mt-2">Collaborate with other workers on projects</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Button
            onClick={() => navigate('/worker-teams/create')}
            className="h-14 px-6 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none"
            data-testid="create-team-btn"
          >
            <Plus size={24} weight="bold" className="mr-2" />
            Create Team
          </Button>
          <Button
            onClick={() => navigate('/browse-worker-teams')}
            variant="outline"
            className="h-14 px-6 font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none"
            data-testid="browse-teams-btn"
          >
            <MagnifyingGlass size={24} weight="bold" className="mr-2" />
            Browse Teams
          </Button>
        </div>

        {/* Pending Invites */}
        {pendingInvites.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <Envelope size={24} weight="bold" className="text-[#2B4C6F]" />
              Pending Invitations ({pendingInvites.length})
            </h2>
            <div className="space-y-3">
              {pendingInvites.map((invite) => (
                <div 
                  key={invite.id}
                  className="bg-white border-2 border-[#2B4C6F] rounded-xl p-4 shadow-[3px_3px_0px_#2B4C6F]"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-gray-900">{invite.team_name}</p>
                      <p className="text-sm text-gray-500">
                        Invited by {invite.inviter_name} {invite.role && `• Role: ${invite.role}`}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleAcceptInvite(invite.id)}
                        disabled={processingInvite === invite.id}
                        className="bg-green-600 hover:bg-green-700 h-10 px-4"
                        data-testid={`accept-invite-${invite.id}`}
                      >
                        {processingInvite === invite.id ? (
                          <SpinnerGap size={16} className="animate-spin" />
                        ) : (
                          <><Check size={16} weight="bold" className="mr-1" /> Accept</>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleDeclineInvite(invite.id)}
                        disabled={processingInvite === invite.id}
                        variant="outline"
                        className="h-10 px-4 border-2 border-red-300 text-red-600 hover:bg-red-50"
                        data-testid={`decline-invite-${invite.id}`}
                      >
                        <X size={16} weight="bold" className="mr-1" /> Decline
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Teams I Lead */}
        <div className="mb-8">
          <h2 className="text-xl font-black text-gray-900 mb-4 flex items-center gap-2">
            <Crown size={24} weight="fill" className="text-amber-500" />
            Teams I Lead
          </h2>
          {ledTeams.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
              <Users size={48} weight="light" className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-bold mb-4">You haven't created a team yet</p>
              <Button
                onClick={() => navigate('/worker-teams/create')}
                className="bg-[#2B4C6F] hover:bg-[#203A55]"
              >
                <Plus size={16} weight="bold" className="mr-2" />
                Create Your First Team
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ledTeams.map((team) => (
                <TeamCard key={team.id} team={team} isLeader={true} />
              ))}
            </div>
          )}
        </div>

        {/* Teams I'm a Member Of */}
        <div>
          <h2 className="text-xl font-black text-gray-900 mb-4 flex items-center gap-2">
            <Users size={24} weight="bold" className="text-[#2B4C6F]" />
            Teams I'm Part Of
          </h2>
          {memberTeams.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
              <UserPlus size={48} weight="light" className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-bold mb-4">You haven't joined any teams yet</p>
              <Button
                onClick={() => navigate('/browse-worker-teams')}
                variant="outline"
                className="border-2 border-black"
              >
                <MagnifyingGlass size={16} weight="bold" className="mr-2" />
                Browse Available Teams
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {memberTeams.map((team) => (
                <TeamCard key={team.id} team={team} isLeader={false} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkerTeams;
